<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="true"
                :showAddNew="true"
                title="IPD Statement"
                btnTitle="Print"
                :showExtraBtn="false"
                @onClickAddNewButton="goToPrint"
                @onClickCloseButton="closeButton"
            />
        </div>

        <div v-if="patientDetails.patient" class="row p-1 px-2">
            <div class="col-md-6">
                <p>Patient Name: {{ patientDetails.patient.full_name }}</p>		
	            <p>Patient ID: {{ patientDetails.patient.patient_id }}</p>		
	            <p class="capitalize">
                    {{ 
                        (patientDetails.patient.birthday ? 'Age: ' + calculateAge(patientDetails.patient.birthday) : '') + 
                        (patientDetails.patient.gender ? ' | Sex: ' + patientDetails.patient.gender : '') 
                    }}
                </p>	
	            <p>Patient Phone No:	{{ patientDetails.patient.mobile_no }}</p>	
	            <p>Patient Address: {{ patientDetails.patient.full_address }}</p>		
            </div>

            <div class="col-md-6">
	            <p>IPD admission no: {{ patientDetails.admission_no }}</p>
	            <p>Admission date: {{ patientDetails.admission_date }}</p>
	            <p>Admission Dept.: {{ patientDetails.department ? patientDetails.department.name : 'N/A' }}</p>
	            <p>Bed/Cabin no: {{ patientDetails.service_resource.name }}</p>
                <p>Referred Doctor: {{ patientDetails.human_resource ? patientDetails.human_resource.name : '' }}</p>
	            <p>{{ patientDetails.consultant ? 'Consultant: ' + patientDetails.consultant.name : '' }}</p>
            </div>
        </div>

        <div class="px-2">
          <ul class="nav nav-tabs">
            <li v-for="tab in tabs" :key="tab.id" class="nav-item">
              <a
                  :class="['nav-link', { active: activeTab.id == tab.id }]"
                  @click="setActiveTab(tab)"
              >
                {{ tab.title }}
              </a>
            </li>
          </ul>
        </div>

        <div class="col-12 px-2 mt-2 mb-5">
            <component :is="tabComponents[activeTab.id]"
                :service="service" 
                :receiptGenerals="receiptGenerals" 
                :total="total" 
                :patientDetails="patientDetails" 
                :companyInfo="companyInfo"
            />
        </div>

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
    import TitleButton from '@/components/atom/TitleButton';
    import Statement from '@/components/molecule/company/hospital/ipd-station/IpdStatementTable.vue';
    import MoneyReceipts from "@/components/molecule/company/hospital/patient/register/tabs/MoneyReceipts";
    import IPDBillListTable from '@/views/page/company/hospital/billing/ipd-billing/IPDBillingLIst.vue';
    import { useRoute, useRouter } from "vue-router";
    import { onMounted, ref } from 'vue'; 
    import handleHospital from '@/services/modules/hospital';
    import Loader from '@/components/atom/LoaderComponent';
    import ipdStatementHelper from '@/services/utils/pdf/ipdStatementHelper';
    import handleCompany from "@/services/modules/company";
    import DentalIpdBilling from '@/components/molecule/company/dental-os/service/billing/DentalIpdBilling.vue';

    const $route = useRoute();
    const $router = useRouter();

    const { fetchIpdStatement, calculateAge } = handleHospital();
    const { generatePdf } = ipdStatementHelper();
    const { fetchCompanyInfo } = handleCompany();

    const patientDetails = ref({});
    const service = ref([]);
    const loading = ref(false);
    const receiptGenerals = ref([]);
    const total = ref({});
    const companyInfo = ref({});

    const tabs = [
      { id: 'statement', title: 'Statements' },
      { id: 'ipd-bill', title: 'Bill' },
      { id: 'money-receipts', title: 'MoneyReceipts' },
    ];

    const tabComponents = {
      'statement': Statement,
      'money-receipts': MoneyReceipts,
      'ipd-bill': $route.name === 'dental-service' ? DentalIpdBilling : IPDBillListTable
    };

    const activeTab = ref(tabs[0]);

    const getQuery = () => {

        const ipdRegisterId = $route.params.ipdRegisterId || $route.query.ipd_register_id;

        let query = `/${ipdRegisterId}?company_id=${$route.params.companyId}`;
        return query;
    }

    onMounted(() => {
        const query = getQuery();
        loading.value = true;

        Promise.all([
            fetchIpdStatement(query).then((res) => {
                patientDetails.value = res.data;
                service.value = res.service_data;
                receiptGenerals.value = res.receipt_generals;
                total.value = res.total;
                total.value.settlement_discount = res.data.settlement_discount;
            }),
            fetchCompanyInfo($route.params.companyId).then((res) => {
                companyInfo.value = res.data
            })
        ]).catch(err => {
            console.log(err)
        }).finally(() => {
            loading.value = false;
        })
    })

    const goToPrint = () => {
        generatePdf(companyInfo.value, patientDetails.value, service.value, receiptGenerals.value, total.value)
    }

    const closeButton = () => {
        $router.back();
    }

    const setActiveTab = (tab) => {
        activeTab.value = tab;
        const routerObject = {
            path: $route.path,
            query: {
              ...$route.query,
              tab: tab.id
            }
        };

        if ($route.hash) {
            routerObject.hash = $route.hash;
        }

        $router.replace(routerObject);
    };

    onMounted(() => {
        const currentTab = $route.query.tab || 'statement';

        if(! currentTab || ! tabs.map(tab => tab.id).includes(currentTab)) return;
        activeTab.value = tabs.find(tab => tab.id === currentTab);
    })
</script>